import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import {
  useEffect,
  useMemo,
  useState,
  createRef,
  Dispatch,
  SetStateAction,
  useRef,
  useLayoutEffect,
} from 'react'
import { WeekStartsOn } from '@opallabs/concepts'

type CalendarDate = {
  ref: React.RefObject<HTMLTableCellElement>
  date: Date
}

type CalendarDayKey = string
type CalendarDateMap = Record<CalendarDayKey, CalendarDate>
type CalendarDateGrid = Array<Array<CalendarDate>>

export type UseCalendarDatesParams = {
  initialFocusDate?: Date
  weekStartsOn: WeekStartsOn
}

export type UseCalendarDatesValue = {
  /** Advance date an amount specified by `action` and focus the relevant cell */
  advanceFromKeypress: (action: DateActions) => void
  /** 2D Array of all Dates to be displayed by a calendar. */
  calendarDateGrid: CalendarDateGrid
  /** Target date for keyboard focus and reference point for building the calendar grid */
  focusDate: Date
  /** Update the focused date to any date. Does not attempt to focus the new date cell. */
  setFocusDate: Dispatch<SetStateAction<Date>>
}

export enum DateActions {
  NextDay = 'NextDay',
  NextMonth = 'NextMonth',
  NextWeek = 'NextWeek',
  NextYear = 'NextYear',
  PrevDay = 'PrevDay',
  PrevMonth = 'PrevMonth',
  PrevWeek = 'PrevWeek',
  PrevYear = 'PrevYear',
}

/**
 * Advances the given date by the amount specified by the given action.
 */
const advanceDate = (date: Date, action: DateActions): Date => {
  switch (action) {
    case DateActions.NextDay:
      return addDays(date, 1)
    case DateActions.NextWeek:
      return addWeeks(date, 1)
    case DateActions.NextMonth:
      return addMonths(date, 1)
    case DateActions.NextYear:
      return addYears(date, 1)
    case DateActions.PrevDay:
      return addDays(date, -1)
    case DateActions.PrevWeek:
      return addWeeks(date, -1)
    case DateActions.PrevMonth:
      return addMonths(date, -1)
    case DateActions.PrevYear:
      return addYears(date, -1)
    default:
      throw new Error(`Unknown DateAction provided ${action}`)
  }
}

export const useCalendarDates = ({
  initialFocusDate = new Date(),
  weekStartsOn,
}: UseCalendarDatesParams): UseCalendarDatesValue => {
  const [focusDate, setFocusDate] = useState<Date>(initialFocusDate)
  // Using the display month to determine when to recalculate our date grid.
  // Since Date is an object, we need to do a bit of work to memoize the
  // value and prevent unnecessary rebuilds of the date grid.
  const [focusMonth, setFocusMonth] = useState<Date>(startOfMonth(focusDate))

  /**
   *  The next date to focus may not be currently rendered on the
   *  calendar, so we can't reliably focus the cell before the component
   *  has had a chance to rerender.
   *  Storing the next date to be focused in a ref will allow us to defer
   *  until the next `useLayoutEffect` runs and the new set of
   *  dates have had a chance to be rendered.
   */
  const nextFocusOperation = useRef<string | null>(null)

  useEffect(() => {
    if (!isSameMonth(focusDate, focusMonth)) {
      setFocusMonth(startOfMonth(focusDate))
    }
  }, [focusMonth, focusDate])

  const [calendarDateGrid, calendarDateMap] = useMemo(
    () => buildDateGrid(focusMonth, weekStartsOn),
    [focusMonth, weekStartsOn]
  )

  useLayoutEffect(() => {
    if (typeof nextFocusOperation.current === 'string') {
      calendarDateMap[nextFocusOperation.current]?.ref.current?.focus()
      nextFocusOperation.current = null
    }
  })

  const advanceFromKeypress = (action: DateActions) => {
    const nextDate = advanceDate(focusDate, action)
    // Setting the focusMonth before the focusDate prevents
    // a flicker in the date selection when the next focus date
    // is displayed on the calendar, but in a different month.
    // Setting in advance gives the calendar a change to rerender
    // the new month before focusing the next date.
    if (!isSameMonth(nextDate, focusMonth)) {
      setFocusMonth(startOfMonth(nextDate))
    }
    setFocusDate(nextDate)
    nextFocusOperation.current = makeDateKey(nextDate)
  }

  return {
    advanceFromKeypress,
    calendarDateGrid,
    focusDate,
    setFocusDate,
  }
}

const WEEKS_PER_GRID = 6

const makeDateKey = (date: Date) => format(date, 'yyyyMMdd') //`${Year}${Month}${DayOfMonth}`

const buildDateGrid = (
  focusDate: Date,
  weekStartsOn: WeekStartsOn
): [CalendarDateGrid, CalendarDateMap] => {
  // Months span 4 to 6 weeks. Always showing 6 keeps the calendar's height
  // fixed, so a popover holding it doesn't reposition on every month change.
  const firstWeek = startOfWeek(startOfMonth(focusDate), { weekStartsOn })
  const weeksInMonth = eachWeekOfInterval(
    {
      start: firstWeek,
      end: addWeeks(firstWeek, WEEKS_PER_GRID - 1),
    },
    { weekStartsOn }
  )

  return weeksInMonth.reduce<[CalendarDateGrid, CalendarDateMap]>(
    ([grid, map], firstDayOfWeek) => {
      const daysOfWeek = eachDayOfInterval({
        start: firstDayOfWeek,
        end: endOfWeek(firstDayOfWeek, { weekStartsOn }),
      })

      const dateArray = daysOfWeek.map<CalendarDate>((dayOfWeek) => {
        const keyOfDay = makeDateKey(dayOfWeek)
        const day: CalendarDate = {
          ref: createRef<HTMLTableCellElement>(),
          date: dayOfWeek,
        }

        map[keyOfDay] = day

        return day
      })

      grid.push(dateArray)

      return [grid, map]
    },
    [[] as CalendarDateGrid, {} as CalendarDateMap]
  )
}
